import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
} from '@mui/material';
import api from '../api';
import FeedbackCard from '../components/user/FeedbackCard';

const ReviewPage = () => {
  const { shareableLink } = useParams();
  const [qaPairs, setQaPairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(undefined);
  const [questionColumn, setQuestionColumn] = useState('Text');
  const [answerColumn, setAnswerColumn] = useState('Answer');
  const [reviewerEmail, setReviewerEmail] = useState('');
  const [isNameSet, setIsNameSet] = useState(false);
  const [submittedIds, setSubmittedIds] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'not_completed', 'completed'
  const [selectedCategory, setSelectedCategory] = useState('');
  const [availableCategories, setAvailableCategories] = useState([]);

  useEffect(() => {
    // Check saved email on mount
    const savedName = localStorage.getItem('reviewerEmail');
    if (savedName) {
      const email = savedName.trim().toLowerCase();
      setReviewerEmail(email);
      setIsNameSet(true);
      // Ensure reviewer user exists in database
      api.post(`/csv/review/${shareableLink}/reviewer`, { email }).catch(() => {});
    }
  }, [shareableLink]);

  useEffect(() => {
    // Fetch data whenever filters, page, link, or reviewer changes
    const fetchData = async () => {
      if (!isNameSet) return;

      try {
        setLoading(true);

        let url = `/csv/review/${shareableLink}?page=${page}&limit=5`;
        if (selectedCategory) {
          url += `&category=${encodeURIComponent(selectedCategory)}`;
        }
        if (statusFilter !== 'all') {
          url += `&status=${statusFilter}&reviewerEmail=${encodeURIComponent(reviewerEmail)}`;
        }

        const [pageDataRes, submittedIdsRes] = await Promise.all([
          api.get(url),
          api.get(`/feedback/status/${shareableLink}?reviewerEmail=${encodeURIComponent(reviewerEmail)}`),
        ]);

        setQaPairs(Array.isArray(pageDataRes?.data?.rows) ? pageDataRes.data.rows : []);
        setTotalPages(pageDataRes?.data?.totalPages || 0);
        setTotalItems(pageDataRes?.data?.totalItems !== undefined ? pageDataRes.data.totalItems : 0);
        if (Array.isArray(pageDataRes?.data?.categories)) {
          setAvailableCategories(pageDataRes.data.categories);
        }
        setQuestionColumn(pageDataRes?.data?.question_column || 'Text');
        setAnswerColumn(pageDataRes?.data?.answer_column || 'Answer');
        setSubmittedIds(new Set(Array.isArray(submittedIdsRes?.data) ? submittedIdsRes.data : []));
        setError('');
      } catch (err) {
        console.error("Could not load review data:", err);
        setError(err.response?.data?.message || 'Could not load review data. The link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [shareableLink, page, isNameSet, reviewerEmail, statusFilter, selectedCategory]);

  const handleFeedbackSuccess = (qaPairId) => {
    setSubmittedIds((prev) => new Set(prev).add(qaPairId));
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleNameSubmit = async () => {
    if (reviewerEmail.trim() && reviewerEmail.includes('@')) {
      const email = reviewerEmail.trim().toLowerCase();
      try {
        await api.post(`/csv/review/${shareableLink}/reviewer`, { email });
      } catch (err) {
        console.warn('Could not register reviewer:', err);
      }
      localStorage.setItem('reviewerEmail', email);
      setIsNameSet(true);
    }
  };

  const handleResetFilters = () => {
    setStatusFilter('all');
    setSelectedCategory('');
    setPage(1);
  };

  const formatLabel = (label) => (label || '').replace(/_/g, ' ');

  if (loading && isNameSet) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {!isNameSet && (
        <Dialog open={!isNameSet} onClose={(e, reason) => reason !== 'backdropClick'}>
          <DialogTitle>Welcome, Reviewer!</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Please enter your email to begin the review process. This will be saved for future sessions.
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              id="email"
              label="Your Email Address"
              type="email"
              fullWidth
              variant="standard"
              value={reviewerEmail}
              onChange={(e) => setReviewerEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleNameSubmit} variant="contained">
              Start Review
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Typography variant="h4" gutterBottom align="center">
        Review and Provide Feedback
      </Typography>

      {/* Filter Bar */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: '#f0f4f8' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="status-filter-label">Review Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Review Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">All Items</MenuItem>
                <MenuItem value="not_completed">Not Completed</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>

            {availableCategories.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 200, maxWidth: 350 }}>
                <InputLabel id="category-filter-label">Filter by Category</InputLabel>
                <Select
                  labelId="category-filter-label"
                  id="category-filter"
                  value={selectedCategory}
                  label="Filter by Category"
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">
                    <em>All Categories ({availableCategories.length})</em>
                  </MenuItem>
                  {availableCategories.map((cat, idx) => (
                    <MenuItem key={idx} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {(statusFilter !== 'all' || selectedCategory !== '') && (
              <Button size="small" variant="text" onClick={handleResetFilters}>
                Reset Filters
              </Button>
            )}
          </Box>

          {totalItems !== undefined && (
            <Chip
              label={`${totalItems} ${totalItems === 1 ? 'item' : 'items'} found`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      </Paper>

      {Array.isArray(qaPairs) && qaPairs.length > 0 ? (
        qaPairs.map((pair) => (
          <FeedbackCard
            key={pair.id}
            qaPair={pair}
            questionLabel={formatLabel(questionColumn)}
            answerLabel={formatLabel(answerColumn)}
            reviewerEmail={reviewerEmail}
            onFeedbackSuccess={handleFeedbackSuccess}
            isSubmitted={submittedIds.has(pair.id)}
          />
        ))
      ) : (
        <Typography align="center" sx={{ mt: 4, fontStyle: 'italic' }}>
          {statusFilter === 'not_completed'
            ? 'All items matching this filter have been reviewed.'
            : 'No items match the selected filter criteria.'}
        </Typography>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Container>
  );
};

export default ReviewPage;